import { useEffect, useMemo, useState } from 'react';
import { useOpsPage } from '../hooks/useDailyInstallations';
import {
  BRAND, Note, PageHeader, Panel, States, Stat,
  dec, gbp, monthName, num, pct, selectClass, thisMonth,
} from '../components/OpsPageKit';

// Customer Experience — the NEO2 rebuild.
//
// Trustpilot reviews per engineer and the top-up they earn (from 01/10/2026):
// 11-20 reviews £25, 21-30 £35, 31-40 £45, 41-50 £55, 51+ £65 (0-10 earns
// nothing). The bands come from the server. Reviews are submitted once a month
// at month end, so the page opens on the last month that has any rather than
// an empty current one.
const TIERS = {
  65: { bg: '#DCFCE7', fg: '#166534' },
  55: { bg: '#D1FAE5', fg: '#047857' },
  45: { bg: '#E0F2FE', fg: '#075985' },
  35: { bg: '#E9D8F0', fg: '#581678' },
  25: { bg: '#F3F4F6', fg: '#374151' },
};
const bandLabel = (tiers, amount) => {
  const i = (tiers || []).findIndex((t) => t.amount === amount);
  if (i < 0) return '';
  const lo = tiers[i].min;
  const hi = i > 0 ? tiers[i - 1].min - 1 : null;
  return hi ? `${lo}–${hi} reviews` : `${lo}+ reviews`;
};

function TierPill({ voucher }) {
  if (!voucher) return <span className="text-xs text-gray-300">—</span>;
  const t = TIERS[voucher] || TIERS[25];
  return (
    <span
      className="inline-block rounded-full px-2 py-0.5 text-[11px] font-semibold"
      style={{ background: t.bg, color: t.fg }}
    >
      {`£${voucher}`}
    </span>
  );
}

function CustomerExperience() {
  const [month, setMonth] = useState(thisMonth());
  const [contract, setContract] = useState('');
  const [jumped, setJumped] = useState(false);

  const filters = useMemo(() => ({ month, contract }), [month, contract]);
  const {
    data, isLoading, isFetching, refetch, isError,
  } = useOpsPage('customer-experience', filters);

  // Reviews land at month end, so an empty current month is expected rather
  // than broken. Move to the last month that has them, once.
  const latest = data?.caveats?.latest_month_with_reviews;
  useEffect(() => {
    if (!jumped && latest && data && data.totals.reviews === 0 && latest !== month) {
      setMonth(latest);
      setJumped(true);
    }
  }, [latest, data, month, jumped]);

  const t = data?.totals || {};
  const c = data?.caveats || {};
  const rows = data?.engineers || [];
  const maxReviews = Math.max(1, ...rows.map((r) => r.reviews));

  return (
    <div className="p-6">
      <PageHeader
        title="Customer Experience"
        subtitle="Trustpilot reviews by engineer, and the top-up each earns."
        month={month}
        onMonth={(m) => { setMonth(m); setJumped(true); }}
        onRefresh={refetch}
        fetching={isFetching}
      >
        <select value={contract} onChange={(e) => setContract(e.target.value)} className={selectClass}>
          <option value="">Both contracts</option>
          <option value="BRSK">BRSK</option>
          <option value="Netomnia">Netomnia</option>
        </select>
      </PageHeader>

      <States isLoading={isLoading} isError={isError} what="customer experience" />

      {data && (
        <>
          <div className="mt-5 grid grid-cols-2 gap-3 lg:grid-cols-4">
            <Stat
              label="Top engineer"
              value={t.top ? t.top.engineer.split(' ')[0] : '—'}
              sub={t.top ? `${num(t.top.reviews)} reviews` : ''}
              accent
            />
            <Stat label="Reviews" value={num(t.reviews)} sub={`${t.engineers} engineers`} />
            <Stat label="Winners" value={num(t.winners)} sub="11 or more reviews" tone="#166534" />
            <Stat label="Top-up total" value={gbp(t.voucher_cost)} sub={`${num(t.winners)} top-ups`} accent />
          </div>

          {t.tiers ? (
            <div className="mt-3 flex flex-wrap gap-2">
              {t.tiers.map((tier) => (
                <span
                  key={tier.amount}
                  className="inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold"
                  style={{ background: TIERS[tier.amount]?.bg, color: TIERS[tier.amount]?.fg }}
                  title={bandLabel(t.tiers, tier.amount)}
                >
                  £{tier.amount}
                  <span className="font-normal">{bandLabel(t.tiers, tier.amount)}</span>
                  <span className="rounded-full bg-white/70 px-1.5 tabular-nums">{num(t.by_tier?.[tier.amount] || 0)}</span>
                </span>
              ))}
            </div>
          ) : null}

          {t.reviews === 0 ? (
            <Note tone="brand">
              No reviews submitted for {monthName(month)} yet. Trustpilot figures are entered once
              a month, at month end
              {c.latest_month_with_reviews
                ? ` — the last month with any is ${monthName(c.latest_month_with_reviews)}.` : '.'}
            </Note>
          ) : null}

          <div className="mt-4">
            <Panel
              title="Reviews by engineer"
              right={<span className="text-xs text-gray-400">{num(rows.length)} with reviews</span>}
            >
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 text-sm">
                  <thead className="bg-gray-50 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                    <tr>
                      <th className="px-3 py-2 w-10" />
                      <th className="px-3 py-2">Engineer</th>
                      <th className="px-3 py-2">Region</th>
                      <th className="px-3 py-2 text-right">Reviews</th>
                      <th className="px-3 py-2 w-40" />
                      <th className="px-3 py-2 text-right">Installs</th>
                      <th className="px-3 py-2 text-right">Per install</th>
                      <th className="px-3 py-2">Top-up</th>
                      <th className="px-3 py-2 text-right">To next tier</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {rows.map((r, i) => (
                      <tr key={r.email || r.engineer} className={i < 3 ? 'bg-brand-light/40' : 'hover:bg-gray-50'}>
                        <td className="px-3 py-1.5 text-right text-xs tabular-nums text-gray-400">{i + 1}</td>
                        <td className="px-3 py-1.5 font-medium text-gray-800">{r.engineer}</td>
                        <td className="px-3 py-1.5 text-xs text-gray-500">{r.region || '—'}</td>
                        <td className="px-3 py-1.5 text-right font-semibold tabular-nums" style={{ color: BRAND }}>
                          {num(r.reviews)}
                        </td>
                        <td className="px-3 py-1.5">
                          <div className="h-1.5 w-full overflow-hidden rounded-full bg-gray-100">
                            <div
                              className="h-full rounded-full"
                              style={{ width: `${(r.reviews / maxReviews) * 100}%`, background: BRAND }}
                            />
                          </div>
                        </td>
                        <td className="px-3 py-1.5 text-right tabular-nums text-gray-500">
                          {r.completed ? num(r.completed) : '—'}
                        </td>
                        <td className="px-3 py-1.5 text-right tabular-nums text-gray-500">
                          {r.review_rate === null ? '—' : pct(r.review_rate)}
                        </td>
                        <td className="px-3 py-1.5"><TierPill voucher={r.voucher} /></td>
                        <td className="px-3 py-1.5 text-right tabular-nums text-gray-400">
                          {r.to_next_tier === null ? '—' : dec(r.to_next_tier, 0)}
                        </td>
                      </tr>
                    ))}
                    {!rows.length ? (
                      <tr>
                        <td colSpan={9} className="px-3 py-10 text-center text-gray-400">
                          No reviews recorded for this month.
                        </td>
                      </tr>
                    ) : null}
                  </tbody>
                </table>
              </div>
            </Panel>
          </div>

          <Note>
            Reviews per install needs completions from the installations mirror, which holds a
            rolling window starting{' '}
            {c.completions_from ? new Date(c.completions_from).toLocaleDateString('en-GB') : 'recently'}
            . {c.completions_available
              ? 'This month is covered.'
              : 'This month falls outside it, so the per-install column is blank rather than wrong.'}
          </Note>
        </>
      )}
    </div>
  );
}

export default CustomerExperience;
